import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useEffect, useMemo } from 'react'

import { getInitialContactData, IContact } from 'clickNgo/entities/contact'
import { IPartner } from 'clickNgo/entities/partner/models'
import { useAppSource } from 'clickNgo/hooks/utils/useAppSource'
import { resetAllModifiedDataByKey, useGetFormData } from 'cng-common/features/form'

import { useCommonStep, useGeneralStep, useHPRolesStep } from '../components/steps'
import { CONTACT_CREATOR_FORM_KEYS, ContactCreatorFormKeys, ContactCreatorSteps } from '../configs'
import { IContactCreatorSettingsForm } from '../models'

interface IParams {
  partner: IPartner
  initialValues?: Partial<IContact>
  onClose: () => void
  onCreate: (contact: IContact) => Promise<void> | void
  onReactivate: (contact: IContact) => void
  isReplacementContact?: boolean
  source?: string
}

export const useContactCreatorWizard = ({
  partner,
  initialValues,
  onCreate,
  onClose,
  onReactivate,
  isReplacementContact = false,
  source,
}: IParams) => {
  const dispatch = useAppDispatch()
  const appSource = useAppSource(source)
  const contact = useMemo<IContact>(
    () => ({ ...getInitialContactData(partner), ...initialValues }),
    [partner, initialValues]
  )

  const initialSettings: IContactCreatorSettingsForm = useMemo(
    () => ({
      actualData: {
        contact,
        partner,
      },
      errorMessages: [],
      isNextStepDisabled: false,
      isPreviousStepDisabled: false,
      currentStep: ContactCreatorSteps.General,
      duplicateHPRoles: [],
    }),
    [contact, partner]
  )

  useGetFormData(ContactCreatorFormKeys.Settings, initialSettings)

  const { additionalStep } = useCommonStep({
    contact,
    partner,
    formKeys: CONTACT_CREATOR_FORM_KEYS,
  })
  const { generalStep } = useGeneralStep({
    contact,
    partner,
    onReactivate,
    formKeys: CONTACT_CREATOR_FORM_KEYS,
  })
  const { hpRolesStep } = useHPRolesStep({
    contact,
    partner,
    formKeys: CONTACT_CREATOR_FORM_KEYS,
    onCreate,
    onClose,
    isReplacementContact,
    source: appSource,
  })

  const steps = useMemo(
    () => [generalStep, additionalStep, hpRolesStep],
    [additionalStep, generalStep, hpRolesStep]
  )

  useEffect(() => {
    return () => {
      Object.values(ContactCreatorFormKeys).forEach((key) => {
        dispatch(resetAllModifiedDataByKey(key))
      })
    }
  }, [dispatch])

  return { steps }
}
